import {
  Badge,
  BlockStack,
  Box,
  Button,
  Card,
  Divider,
  EmptyState,
  InlineStack,
  Page,
  SkeletonBodyText,
  Text,
  TextField,
} from "@shopify/polaris";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";

import { ApiError } from "../api/client";
import { getMe } from "../api/me";
import {
  listSuppliers,
  setSupplierContactEmail,
  setSupplierInvoiceNo,
  type SupplierOption,
} from "../api/purchase_orders";
import { AddSupplierModal } from "../components/po/AddSupplierModal";
import { GatedQueryError } from "../components/GatedQueryError";
import {
  genericRegistrationNoError,
  invoiceNoError,
  isValidGenericRegistrationNo,
  isValidInvoiceNo,
} from "../utils/invoiceNo";
import {
  registrationConcept,
  type RegistrationConcept,
} from "../utils/registrationLabel";

import { t } from "../lib/i18n";

/** ?add=1 で追加モーダルを開いた状態で着地する（未割当セレクトの「＋新規」から飛ぶ）。 */
const ADD_PARAM = "add";

type InlineFieldProps = {
  label: string;
  value: string | null;
  placeholder: string;
  /** 空文字は null（消去）として保存する。 */
  onSave: (value: string | null) => Promise<unknown>;
  validate?: (value: string) => string | undefined;
  emptyText: string;
};

/**
 * クリック→入力欄→blur/Enter で保存のインライン編集（確定後PO編集と同じ作法・
 * 確認ダイアログなし）。失敗時はエラーを出して編集状態のまま残す。
 */
function InlineField({ label, value, placeholder, onSave, validate, emptyText }: InlineFieldProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [error, setError] = useState<string | undefined>(undefined);
  const [isSaving, setIsSaving] = useState(false);

  const startEdit = () => {
    setDraft(value ?? "");
    setError(undefined);
    setIsEditing(true);
  };

  const save = async () => {
    const trimmed = draft.trim();
    const validationError = validate?.(trimmed);
    if (validationError) {
      setError(validationError);
      return;
    }
    if (trimmed === (value ?? "")) {
      setIsEditing(false);
      return;
    }
    setIsSaving(true);
    try {
      await onSave(trimmed === "" ? null : trimmed);
      setIsEditing(false);
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : t("保存できませんでした。時間をおいて再度お試しください。"));
    } finally {
      setIsSaving(false);
    }
  };

  if (!isEditing) {
    return (
      <InlineStack gap="150" blockAlign="center" wrap={false}>
        {value ? (
          <Text as="span" variant="bodyMd">
            {value}
          </Text>
        ) : (
          <Text as="span" variant="bodyMd" tone="subdued">
            {emptyText}
          </Text>
        )}
        <Button variant="plain" size="micro" onClick={startEdit} accessibilityLabel={t("{label}を編集", { label })}>
          {t("編集")}
        </Button>
      </InlineStack>
    );
  }

  return (
    <Box maxWidth="280px">
      <TextField
        label={label}
        labelHidden
        value={draft}
        onChange={(v) => {
          setDraft(v);
          setError(undefined);
        }}
        onBlur={() => void save()}
        autoComplete="off"
        autoFocus
        placeholder={placeholder}
        error={error}
        disabled={isSaving}
      />
    </Box>
  );
}

type SupplierRowProps = {
  supplier: SupplierOption;
  registration: RegistrationConcept | null;
};

function SupplierRow({ supplier, registration }: SupplierRowProps) {
  const queryClient = useQueryClient();
  const isJapanStore = registration?.isJapanInvoiceSystem === true;

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["po-suppliers"] });
    // 登録番号は発注書 PDF/CSV のヘッダに乗る＝ドラフトの再取得も要る
    queryClient.invalidateQueries({ queryKey: ["purchase-order-drafts"] });
  };

  const invoiceMutation = useMutation({
    mutationFn: (value: string | null) => setSupplierInvoiceNo(supplier.id, value),
    onSuccess: invalidate,
  });
  const emailMutation = useMutation({
    mutationFn: (value: string | null) => setSupplierContactEmail(supplier.id, value),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["po-suppliers"] }),
  });

  const validateRegistration = (value: string): string | undefined => {
    if (value === "") return undefined;
    if (isJapanStore) return isValidInvoiceNo(value) ? undefined : invoiceNoError();
    return isValidGenericRegistrationNo(value) ? undefined : genericRegistrationNoError();
  };

  const assignedCount = supplier.assigned_variant_count;

  return (
    <Box paddingBlockStart="300" paddingBlockEnd="300">
      <BlockStack gap="200">
        <InlineStack gap="200" blockAlign="center">
          <Text as="h3" variant="headingSm">
            {supplier.name}
          </Text>
          {typeof assignedCount === "number" && (
            <Text as="span" variant="bodySm" tone="subdued">
              {t("割当商品 {n} 件", { n: assignedCount })}
            </Text>
          )}
        </InlineStack>

        <InlineStack gap="600" wrap>
          {registration && (
            <BlockStack gap="100">
              <Text as="span" variant="bodySm" tone="subdued">
                {isJapanStore ? t("適格請求書 登録番号") : registration.label}
              </Text>
              <InlineStack gap="150" blockAlign="center">
                {isJapanStore && supplier.invoice_registration_no && (
                  <Badge tone="success">{t("適格請求書発行事業者")}</Badge>
                )}
                {isJapanStore && !supplier.invoice_registration_no && (
                  <Badge tone="attention">{t("未登録")}</Badge>
                )}
                <InlineField
                  label={isJapanStore ? t("適格請求書 登録番号") : registration.label}
                  value={supplier.invoice_registration_no}
                  placeholder={isJapanStore ? "T1234567890123" : ""}
                  emptyText={t("未設定")}
                  validate={validateRegistration}
                  onSave={(value) => invoiceMutation.mutateAsync(value)}
                />
              </InlineStack>
            </BlockStack>
          )}

          <BlockStack gap="100">
            <Text as="span" variant="bodySm" tone="subdued">
              {t("発注書の送信先メールアドレス")}
            </Text>
            <InlineField
              label={t("発注書の送信先メールアドレス")}
              value={supplier.contact_email ?? null}
              placeholder="orders@example.com"
              emptyText={t("未設定")}
              onSave={(value) => emailMutation.mutateAsync(value)}
            />
          </BlockStack>
        </InlineStack>
      </BlockStack>
    </Box>
  );
}

/**
 * 仕入先ページ。一覧・その場編集・追加を1箇所に集める＝「どこから登録するの？」を
 * 二度と起こさない（2026-08-03 の実機踏破で入口が見つからず手が止まった）。
 * データは注文書画面と同じ ["po-suppliers"]＝同期（vendor 自動作成）にもリロード
 * 不要で追従する（Dashboard の同期成功時に invalidate）。
 */
export function Suppliers() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isModalOpen, setIsModalOpen] = useState(searchParams.get(ADD_PARAM) === "1");

  const suppliersQuery = useQuery({ queryKey: ["po-suppliers"], queryFn: listSuppliers });
  const meQuery = useQuery({ queryKey: ["me"], queryFn: getMe });
  const registration = registrationConcept(meQuery.data?.country_code);

  const closeModal = () => {
    setIsModalOpen(false);
    if (searchParams.has(ADD_PARAM)) {
      const next = new URLSearchParams(searchParams);
      next.delete(ADD_PARAM);
      setSearchParams(next, { replace: true });
    }
  };

  const suppliers = suppliersQuery.data ?? [];

  return (
    <Page
      title={t("仕入先")}
      subtitle={suppliers.length > 0 ? t("{n}社", { n: suppliers.length }) : undefined}
      primaryAction={{
        content: t("仕入先を追加"),
        onAction: () => setIsModalOpen(true),
      }}
    >
      <BlockStack gap="400">
        {suppliersQuery.isError ? (
          <GatedQueryError
            title={t("仕入先の取得に失敗しました")}
            onRetry={() => void suppliersQuery.refetch()}
          />
        ) : suppliersQuery.isLoading ? (
          <Card>
            <SkeletonBodyText lines={6} />
          </Card>
        ) : suppliers.length === 0 ? (
          <Card>
            <EmptyState
              heading={t("仕入先が未登録です")}
              action={{
                content: t("仕入先を追加"),
                onAction: () => setIsModalOpen(true),
              }}
              image=""
            >
              <p>
                {t("仕入先は3つの経路で登録できます。①同期＝Shopify商品のベンダー名から自動作成 ②保存済みStockyデータの取込 ③このページから手動追加。")}
              </p>
            </EmptyState>
          </Card>
        ) : (
          <Card>
            <BlockStack gap="0">
              {suppliers.map((supplier, index) => (
                <BlockStack key={supplier.id} gap="0">
                  {index > 0 && <Divider />}
                  <SupplierRow supplier={supplier} registration={registration} />
                </BlockStack>
              ))}
            </BlockStack>
          </Card>
        )}

        <Text as="p" variant="bodySm" tone="subdued">
          {t("ベンダー（Shopify 商品の属性）とは別物です。同期時にベンダー名から同名の仕入先を自動作成しますが、その後は独立して管理できます（商品ごとの割当は注文書・発注点設定から変更）。")}
        </Text>
      </BlockStack>

      <AddSupplierModal open={isModalOpen} onClose={closeModal} />
    </Page>
  );
}
